import { Controller } from 'stimulus';
import { templateContent } from '@/js/util';

const ROLE_STEP = 0;
const BUSINESS_STEPS = [1, 2];
const CALL_STEP = 3;
const CALENDLY_ORIGIN = 'https://calendly.com';
const MODAL_CLOSE_EVENT = 'ui--twin-turbo-modal:close';

// A step is visible when every step before it (for the selected role) is answered.
// Standard roles walk Q1 → Q2 → Q3 → Q4; Student stops at Q1; Other skips Q2/Q3 and
// reaches Q4 once its free-text role is filled in.
export default class extends Controller {
  static targets = [
    'step',
    'submitButton',
    'otherRoleRadio',
    'otherRoleField',
    'otherRoleInput',
    'callYesRadio',
    'callYesLabel',
    'callNoRadio',
    'callModalTemplate',
  ];

  connect() {
    this.callBooked = false;
    this.boundOnModalClose = this.onModalClose.bind(this);
    this.boundOnCalendlyMessage = this.onCalendlyMessage.bind(this);
    document.addEventListener(MODAL_CLOSE_EVENT, this.boundOnModalClose);
    window.addEventListener('message', this.boundOnCalendlyMessage);

    this.applyVisibility();
    this.applyOtherRoleVisibility();
    this.updateSubmitButton();
  }

  disconnect() {
    document.removeEventListener(MODAL_CLOSE_EVENT, this.boundOnModalClose);
    window.removeEventListener('message', this.boundOnCalendlyMessage);
    this.callModal?.remove();
  }

  selectOption(event) {
    const step = event.target.closest('[data-onboarding-poll-target="step"]');
    const stepIndex = parseInt(step.dataset.step, 10);

    if (stepIndex === ROLE_STEP) {
      // Changing Q1 (role) always resets the downstream flow so users don't
      // carry stale answers from a previous role selection.
      this.clearStepsAfter(ROLE_STEP);
      this.applyOtherRoleVisibility();
    }

    this.applyVisibility();
    this.updateSubmitButton();

    if (stepIndex === CALL_STEP && event.target === this.callYesRadioTarget) {
      this.openCallModal();
    }
  }

  clearStepsAfter(stepIndex) {
    this.stepTargets.forEach((step) => {
      const index = parseInt(step.dataset.step, 10);
      if (index <= stepIndex) {
        return;
      }
      step.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
        input.checked = false;
      });
    });
  }

  applyVisibility() {
    const visibleSteps = this.visibleStepIndexes();

    this.stepTargets.forEach((step) => {
      const index = parseInt(step.dataset.step, 10);
      const visible = visibleSteps.includes(index);
      const wasHidden = step.classList.contains('onboarding-poll__step--hidden');

      if (visible && wasHidden) {
        // Seed opacity:0 and maxHeight:0 before swapping display:none → block so the
        // transition has a real "from" value (elements at display:none don't track one).
        step.style.opacity = '0';
        step.style.maxHeight = '0';
        step.classList.remove('onboarding-poll__step--hidden');
        void step.offsetHeight;
        step.style.opacity = '';
        step.style.maxHeight = '';
      } else {
        step.classList.toggle('onboarding-poll__step--hidden', !visible);
      }

      step.inert = !visible;

      // Disable inputs in hidden steps so they aren't tabbable or submitted,
      // and clear any previously-selected values so the user can't submit a
      // stale answer for a step they no longer see (e.g., picking Designer
      // -> answering Q2 -> changing Q1 to Student).
      step.querySelectorAll('input[type="radio"]').forEach((input) => {
        input.disabled = !visible || this.lockedRadio(input);
        if (!visible) {
          input.checked = false;
        }
      });
    });
  }

  visibleStepIndexes() {
    const role = this.selectedRole();
    if (role === 'student') {
      return [ROLE_STEP];
    }
    if (role === 'other_role') {
      return this.otherRoleFilled() ? [ROLE_STEP, CALL_STEP] : [ROLE_STEP];
    }

    const visible = [ROLE_STEP];
    for (const index of [...BUSINESS_STEPS, CALL_STEP]) {
      if (!this.stepAnswered(index - 1)) {
        break;
      }
      visible.push(index);
    }
    return visible;
  }

  selectedRole() {
    const step = this.stepByIndex(ROLE_STEP);
    return step?.querySelector('input[type="radio"]:checked')?.value;
  }

  stepAnswered(index) {
    const step = this.stepByIndex(index);
    return Boolean(step && step.querySelector('input[type="radio"]:checked'));
  }

  stepByIndex(index) {
    return this.stepTargets.find((step) => parseInt(step.dataset.step, 10) === index);
  }

  otherRoleFilled() {
    return this.hasOtherRoleInputTarget && this.otherRoleInputTarget.value.trim().length > 0;
  }

  // Show the free-text input only when the user picks "Other" for Q1. Hidden
  // and disabled otherwise so it isn't tabbable or submitted with a stale
  // value if the user changes their role selection.
  applyOtherRoleVisibility() {
    if (!this.hasOtherRoleFieldTarget || !this.hasOtherRoleInputTarget) {
      return;
    }

    const visible = this.hasOtherRoleRadioTarget && this.otherRoleRadioTarget.checked;

    this.otherRoleFieldTarget.classList.toggle('onboarding-poll__other-role--hidden', !visible);
    this.otherRoleInputTarget.disabled = !visible;

    if (!visible) {
      this.otherRoleInputTarget.value = '';
    }
  }

  // Typing into the "Other" field reveals Q4 once there is text and hides it again when cleared.
  updateOtherRole() {
    this.applyVisibility();
    this.updateSubmitButton();
  }

  // The TwinTurbo modal opens itself once appended and removes itself after closing.
  // A booked call is final: the answer is locked and the modal never reopens.
  openCallModal() {
    if (this.callBooked || !this.hasCallModalTemplateTarget || this.callModal?.isConnected) {
      return;
    }
    this.callModal = templateContent(this.callModalTemplateTarget);
    document.body.append(this.callModal);
  }

  // Closing the booking modal after a confirmed booking continues the poll. Closing it without
  // one withdraws the "Yes" answer, so the user has to pick again (re-selecting "Yes" reopens
  // the modal) or choose "Not right now".
  onModalClose(event) {
    if (event.target !== this.callModal) {
      return;
    }
    this.callModal = null;
    if (!this.hasCallYesRadioTarget) {
      return;
    }
    if (this.callBooked) {
      this.continueAfterBooking();
      return;
    }
    this.callYesRadioTarget.checked = false;
    this.updateSubmitButton();
  }

  // Calendly posts event_scheduled from inside the iframe once the slot is confirmed. The modal
  // stays open: Calendly may still ask to verify a phone number for SMS reminders, and it posts
  // nothing when that is done, so the user closes the modal when they are finished.
  onCalendlyMessage(event) {
    if (event.origin !== CALENDLY_ORIGIN || event.data?.event !== 'calendly.event_scheduled') {
      return;
    }
    this.callBooked = true;
    this.lockCallAnswer();
  }

  continueAfterBooking() {
    if (this.allRequiredAnswered()) {
      this.submitButtonTarget.form.requestSubmit();
    }
  }

  lockedRadio(input) {
    return this.callBooked && this.hasCallNoRadioTarget && input === this.callNoRadioTarget;
  }

  lockCallAnswer() {
    if (!this.hasCallYesRadioTarget) {
      return;
    }
    this.callYesRadioTarget.checked = true;
    if (this.hasCallYesLabelTarget) {
      this.callYesLabelTarget.textContent = 'Call booked';
    }
    if (this.hasCallNoRadioTarget) {
      this.callNoRadioTarget.disabled = true;
    }
    this.updateSubmitButton();
  }

  updateSubmitButton() {
    if (!this.hasSubmitButtonTarget) {
      return;
    }

    this.submitButtonTarget.disabled = !this.allRequiredAnswered();
  }

  allRequiredAnswered() {
    const radiosAnswered = this.visibleStepIndexes().every((index) => this.stepAnswered(index));
    if (!radiosAnswered) {
      return false;
    }

    // When "Other" is selected for Q1 the free-text input becomes required;
    // otherwise the user could submit an empty other_role_value.
    if (this.hasOtherRoleRadioTarget && this.otherRoleRadioTarget.checked) {
      return this.otherRoleFilled();
    }

    return true;
  }
}
